<script setup>
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';

import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  hasSelection: {
    type: Boolean,
    default: false,
  },
  isEditorMenuPopover: {
    type: Boolean,
    default: false,
  },
  // Signature-aware emptiness is computed by the parent (which has access to
  // the signature + channel context) and passed in as a boolean.
  hasContent: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['executeCopilotAction']);

const { t } = useI18n();

const replyMode = useMapGetter('draftMessages/getReplyEditorMode');

// Selection-based menu items (when text is selected)
const menuItems = computed(() => {
  const items = [];
  // for now, we don't allow improving just  aprt of the selection
  // we will add this feature later. Once we do, we can revert the change
  const hasSelection = false;
  // const hasSelection = props.hasSelection

  if (hasSelection) {
    items.push({
      label: t(
        'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.IMPROVE_REPLY_SELECTION'
      ),
      key: 'improve_selection',
      icon: 'i-hugeicons-pencil-edit-02',
    });
  } else if (
    props.conversationId &&
    replyMode.value === REPLY_EDITOR_MODES.REPLY &&
    props.hasContent
  ) {
    items.push({
      label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.IMPROVE_REPLY'),
      key: 'improve',
      icon: 'i-hugeicons-pencil-edit-02',
    });
  }

  if (props.hasContent) {
    items.push(
      {
        label: t(
          'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.TITLE'
        ),
        key: 'change_tone',
        icon: 'i-hugeicons-text-font',
        subMenuItems: [
          {
            label: t(
              'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.OPTIONS.PROFESSIONAL'
            ),
            key: 'professional',
          },
          {
            label: t(
              'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.OPTIONS.CASUAL'
            ),
            key: 'casual',
          },
          {
            label: t(
              'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.OPTIONS.STRAIGHTFORWARD'
            ),
            key: 'straightforward',
          },
          {
            label: t(
              'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.OPTIONS.CONFIDENT'
            ),
            key: 'confident',
          },
          {
            label: t(
              'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CHANGE_TONE.OPTIONS.FRIENDLY'
            ),
            key: 'friendly',
          },
        ],
      },
      {
        label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.GRAMMAR'),
        key: 'fix_spelling_grammar',
        icon: 'i-hugeicons-text-check',
      }
    );
  }
  return items;
});

const generalMenuItems = computed(() => {
  const items = [];
  if (props.conversationId && replyMode.value === REPLY_EDITOR_MODES.REPLY) {
    items.push({
      label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUGGESTION'),
      key: 'reply_suggestion',
      icon: 'i-hugeicons-ai-chat-02',
    });
  }

  if (
    props.conversationId &&
    (replyMode.value === REPLY_EDITOR_MODES.NOTE || true)
  ) {
    items.push({
      label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUMMARIZE'),
      key: 'summarize',
      icon: 'i-hugeicons-text-align-left',
    });
  }

  items.push({
    label: t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.ASK_COPILOT'),
    key: 'ask_copilot',
    icon: 'i-hugeicons-ai-brain-01',
  });

  return items;
});

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth } = useWindowSize();

// Smart submenu positioning based on available space
const submenuPosition = computed(() => {
  const el = menuRef.value?.$el;
  if (!el) return 'ltr:right-full rtl:left-full';

  const { left, right } = el.getBoundingClientRect();
  const SUBMENU_WIDTH = 200;
  const spaceRight = (windowWidth.value ?? window.innerWidth) - right;
  const spaceLeft = left;

  // Prefer right, fallback to side with more space
  const showRight = spaceRight >= SUBMENU_WIDTH || spaceRight >= spaceLeft;

  return showRight ? 'left-full' : 'right-full';
});

const handleMenuItemClick = item => {
  // For items with submenus, do nothing on click (hover will show submenu)
  if (!item.subMenuItems) {
    emit('executeCopilotAction', item.key);
  }
};

const handleSubMenuItemClick = (parentItem, subItem) => {
  emit('executeCopilotAction', subItem.key);
};
</script>

<template>
  <DropdownBody
    ref="menuRef"
    class="copilot-menu z-50 w-72 max-w-[calc(100vw-2rem)] [&>ul]:gap-1 [&>ul]:p-1.5 [&>ul]:bg-n-solid-2 [&>ul]:shadow-lg"
    :class="{ 'selection-menu': hasSelection && isEditorMenuPopover }"
  >
    <div
      class="flex items-center gap-2 border-b border-n-weak px-2.5 py-2.5 mb-1"
    >
      <Icon icon="i-woot-captain" class="size-4 text-n-blue-11" />
      <span class="text-xs font-semibold text-n-slate-11">{{
        t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.MENU_TITLE')
      }}</span>
    </div>
    <div v-if="menuItems.length > 0" class="flex flex-col gap-1">
      <div
        v-for="item in menuItems"
        :key="item.key"
        class="w-full relative group/submenu"
      >
        <Button
          slate
          link
          sm
          class="copilot-menu-row"
          @click="handleMenuItemClick(item)"
        >
          <!-- prettier-ignore -->
          <span class="copilot-menu-icon">
            <Icon :icon="item.icon" class="size-4" />
          </span>
          <span class="min-w-0 flex-1 text-left">{{ item.label }}</span>
          <Icon
            v-if="item.subMenuItems"
            icon="i-hugeicons-arrow-right-01"
            class="size-3.5 shrink-0 text-n-slate-10"
          />
        </Button>

        <!-- Hover Submenu -->
        <DropdownBody
          v-if="item.subMenuItems"
          class="group-hover/submenu:block group-focus-within/submenu:block hidden [&>ul]:gap-2 [&>ul]:px-3 [&>ul]:py-2.5 [&>ul]:dark:!border-n-strong max-h-[15rem] min-w-32 z-10 top-0"
          :class="submenuPosition"
        >
          <Button
            v-for="subItem in item.subMenuItems"
            :key="subItem.key + subItem.label"
            :label="subItem.label"
            slate
            link
            sm
            class="copilot-menu-row !min-h-9"
            @click="handleSubMenuItemClick(item, subItem)"
          />
        </DropdownBody>
      </div>
    </div>

    <div v-if="menuItems.length > 0" class="my-1 h-px w-full bg-n-weak" />

    <div class="flex flex-col gap-1">
      <Button
        v-for="item in generalMenuItems"
        :key="item.key"
        slate
        link
        sm
        class="copilot-menu-row"
        @click="handleMenuItemClick(item)"
      >
        <!-- prettier-ignore -->
        <span class="copilot-menu-icon">
          <Icon :icon="item.icon" class="size-4" />
        </span>
        <span class="min-w-0 flex-1 text-left">{{ item.label }}</span>
      </Button>
    </div>
  </DropdownBody>
</template>

<style scoped lang="scss">
.copilot-menu-row {
  @apply w-full min-h-11 !h-auto !justify-start !gap-2.5 !rounded-lg !px-2.5 !py-1.5 !text-[13px] !font-medium !text-n-slate-12 !whitespace-normal;
  @apply hover:!no-underline hover:!bg-n-alpha-2 focus-visible:!bg-n-alpha-2 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-n-blue-8;
}
.copilot-menu-icon {
  @apply grid size-7 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11;
}
.copilot-menu-row:hover .copilot-menu-icon,
.copilot-menu-row:focus-visible .copilot-menu-icon {
  @apply bg-n-blue-3 text-n-blue-11;
}

.selection-menu {
  // Fixed + viewport coordinates computed by the editor (positionCopilotMenu),
  // so the menu is never clipped by the composer wrappers.
  position: fixed !important;
  top: var(--copilot-menu-top, 0) !important;
  left: var(--copilot-menu-left, 0) !important;
  right: auto !important;
  bottom: auto !important;
}
</style>
